import moment from 'moment';
import { MENU_ORDER_TYPE } from 'helpers/enums';

/**
 * Get time in 12-hour AM/PM format from newDateStr (e.g. "2026-03-19 22:20" → "10:20 PM").
 *
 * @param {string} newDateStr - Date string in "YYYY-MM-DD HH:mm" (24-hour)
 * @returns {string} Time in 12-hour format, e.g. "10:20 PM"
 */
export function getTimeInAmPmFromDateStr(newDateStr) {
  if (!newDateStr) return '';
  const m = moment(newDateStr, 'YYYY-MM-DD HH:mm');
  return m.isValid() ? m.format('h:mm A') : '';
}

/**
 * Find the closest available time slot from dateSlotObj.times for the given newDateStr.
 * Uses mTimeStamp for comparison. Returns the first slot >= target time, or the last slot if past all.
 *
 * @param {string} newDateStr - Date string in "YYYY-MM-DD HH:mm" (24-hour)
 * @param {Object} dateSlotObj - Slot object with times[] (each with mTimeStamp, displayForUse, etc.)
 * @returns {Object|null} The closest time object, or null if dateSlotObj/times invalid
 */
export function getClosestTimeSlotFromDateStr(newDateStr, dateSlotObj) {
  if (!newDateStr || !dateSlotObj?.times?.length) return null;

  const targetMTimeStamp = moment(newDateStr, 'YYYY-MM-DD HH:mm').valueOf();
  if (!Number.isFinite(targetMTimeStamp)) return null;

  const times = dateSlotObj.times;

  // Find the nearest slot at or after the target time (nearest above)
  const nextSlot = times.find((t) => t.mTimeStamp >= targetMTimeStamp);
  if (nextSlot) return nextSlot;

  // If target is past all slots, return the last one
  return times[times.length - 1];
}

/**
 * Get extra preparation/delivery time in minutes based on order value and configured rules.
 * Uses the highest matching threshold when multiple rules apply.
 *
 * @param {number} amount - Total order value (e.g. payableAmount)
 * @param {Array} rules - Array of { minAmount, extraMinutesPickup, extraMinutesDelivery, isEnabled }
 * @param {string} orderType - MENU_ORDER_TYPE.pickup or MENU_ORDER_TYPE.delivery
 * @returns {number} Extra minutes to add
 */
export function getExtraTimeFromOrderValue(amount, rules, orderType) {
  if (!rules || !Array.isArray(rules) || rules.length === 0) return 0;

  const isPickup = orderType === MENU_ORDER_TYPE.pickup;
  let extraTime = 0;

  // Sort by minAmount ascending so the last matching rule has the highest threshold
  const sortedRules = [...rules].sort(
    (a, b) => (a.minAmount || 0) - (b.minAmount || 0)
  );

  for (const rule of sortedRules) {
    if (rule.isEnabled && Number(amount) >= Number(rule.minAmount || 0)) {
      extraTime = isPickup
        ? Number(rule.extraMinutesPickup ?? 0)
        : Number(rule.extraMinutesDelivery ?? 0);
    }
  }

  return extraTime;
}

/**
 * Get effective preparation time (base + order value adjustment).
 *
 * @param {Object} orderSetup - Store order setup config
 * @param {number} orderAmount - Cart total (payableAmount)
 * @param {string} orderType - MENU_ORDER_TYPE.pickup or MENU_ORDER_TYPE.delivery
 * @returns {{ deliveryTime: number, pickUpTime: number }} Effective base times in minutes
 */
export function getEffectivePreparationTimes(
  orderSetup,
  orderAmount,
  orderType
) {
  const baseDelivery = Number(
    orderSetup?.deliveryTime || orderSetup?.defaultDeliveryTime || 60
  );
  const basePickup = Number(
    orderSetup?.pickUpTime || orderSetup?.defaultPickUpTime || 15
  );

  if (
    !orderSetup?.isOrderValueTimeAdjustEnabled ||
    !orderSetup?.orderTimeRules?.length
  ) {
    return { deliveryTime: baseDelivery, pickUpTime: basePickup };
  }

  const extraDelivery = getExtraTimeFromOrderValue(
    orderAmount,
    orderSetup.orderTimeRules,
    MENU_ORDER_TYPE.delivery
  );
  const extraPickup = getExtraTimeFromOrderValue(
    orderAmount,
    orderSetup.orderTimeRules,
    MENU_ORDER_TYPE.pickup
  );

  return {
    deliveryTime: baseDelivery + extraDelivery,
    pickUpTime: basePickup + extraPickup,
  };
}

